import React from "react";
import "./EventCardV2.css";
import "./button.css";
import { useLanguage } from '../context/LanguageContext'; // Importér LanguageContext

type Props = {
    eventId: number;
    eventName: string;
    eventLogo?: string;
    startTime: string;
    endTime: string;
    onSignup: (eventId: number) => void;
    onViewShifts?: (eventId: number) => void; // 👈 NY
    signupTextDa?: string;
    signupTextEn?: string;
    viewShiftsTextDa?: string;
    viewShiftsTextEn?: string;
    showDate?: boolean;
    badgeText?: string;
    isActive?: boolean;
};

function startOfDay(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function daysDiff(from: Date, to: Date) {
    const a = startOfDay(from).getTime();
    const b = startOfDay(to).getTime();
    const ms = 24 * 60 * 60 * 1000;
    return Math.round((b - a) / ms);
}

function daysLabel(days: number) {
    if (days === 0) return "i dag";
    if (days === 1) return "i morgen";
    if (days > 1) return `om ${days} dage`;
    if (days === -1) return "i går";
    return `${Math.abs(days)} dage siden`;
}

const EventCardV2: React.FC<Props> = ({
    eventId,
    eventName,
    eventLogo,
    startTime,
    endTime,
    onSignup,
    onViewShifts, // 👈 NY
    signupTextDa = "TILMELD/SE DINE VAGTER",
    signupTextEn = "SIGNUP/LOGIN",

    viewShiftsTextDa = "Se alle ledige vagter",
    viewShiftsTextEn = "View all available shifts",
    showDate = true,
    badgeText,
    isActive = true,
}) => {
    const hasImage = !!eventLogo && eventLogo.trim().length > 0;

    const { language } = useLanguage(); // Brug sprog fra LanguageContext

    const start = new Date(startTime);
    const end = new Date(endTime);

    const isValidStart = !isNaN(start.getTime());
    const isValidEnd = !isNaN(end.getTime());

    const fmtDate = (d: Date) =>
        new Intl.DateTimeFormat("da-DK", {
            day: "numeric",
            month: "short",
            year: "numeric",
        }).format(d);

    const startDate = isValidStart ? fmtDate(start) : "Ukendt start";
    const endDate = isValidEnd ? fmtDate(end) : "Ukendt slut";

    const sameDay =
        isValidStart &&
        isValidEnd &&
        start.getFullYear() === end.getFullYear() &&
        start.getMonth() === end.getMonth() &&
        start.getDate() === end.getDate();

    const datePretty = sameDay ? startDate : `${startDate} – ${endDate}`;

    const d = isValidStart ? daysDiff(new Date(), start) : null;
    const countdown = d === null ? null : daysLabel(d);

    return (
        <div
            className={`eventCardV2 ${!isActive ? "isDisabled" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => isActive && onSignup(eventId)}
            onKeyDown={(e) => {
                if (!isActive) return;
                if (e.key === "Enter" || e.key === " ") onSignup(eventId);
            }}
            aria-label={`Open event ${eventName}`}
        >
            <div className="eventCardV2__media">
                {hasImage ? (
                    <img className="eventCardV2__img" src={eventLogo} alt={eventName} />
                ) : (
                    <div className="eventCardV2__imgFallback">
                        <div className="eventCardV2__fallbackMark" />
                    </div>
                )}

                <div className="eventCardV2__mediaOverlay" />

                <div className="eventCardV2__badges">
                    {/* Countdown pill (i stedet for TEAM) */}

                    {countdown && showDate &&(
                        <span className="eventCardV2__pill eventCardV2__pill--countdown">
                            {countdown}
                        </span>
                    )}

                    {/* Hvis du stadig vil bruge badgeText, så gør den “pæn” */}
                    {badgeText && (
                        <span className="eventCardV2__pill eventCardV2__pill--tag">
                            {badgeText}
                        </span>
                    )}

                    <span className={`eventCardV2__pill ${isActive ? "isOk" : "isOff"}`}>
                        {isActive ? "ACTIVE" : "INACTIVE"}
                    </span>
                </div>
            </div>

            <div className="eventCardV2__body">
                <div className="eventCardV2__title" title={eventName}>
                    {eventName}
                </div>

                {/* Dato stadig der – men mere lækkert */}
                {showDate && (
                    <div className="eventCardV2__meta">
                        <div className="eventCardV2__date">{datePretty}</div>
                    </div>
                )}


                <button
                    className={`eventCardV2__btn ${!isActive ? "isDisabled" : ""}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (isActive) onSignup(eventId);
                    }}
                    disabled={!isActive}
                >
                    {language === "da"
                        ? signupTextDa
                        : signupTextEn}
                </button>
                {onViewShifts && (
                    <button
                        className="eventCardV2__btn secondary"
                        onClick={(e) => {
                            e.stopPropagation();
                            onViewShifts(eventId);
                        }}
                    >
                        {language === "da"
                            ? viewShiftsTextDa
                            : viewShiftsTextEn}
                    </button>
                )}

            </div>
        </div>
    );
};

export default EventCardV2;
